import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Segmented,
} from '@smena/ui';
import { QueryState } from '@/components/QueryState';
import {
  addVendorIgnored,
  fetchVendorIgnored,
  removeVendorIgnored,
} from '@/features/ignored-chats/api';

/**
 * Карточка «Игнорируемые чаты» на странице «Система».
 *
 * Здесь только ПЛАТФОРМЕННЫЕ правила: служебные боты мессенджеров, одинаковые у всех
 * клиентов. Живой повод завести экран: агент пять раз подряд ответил боту «MAX Поддержка»
 * на «переформулируйте запрос» - кроме токенов это спам в поддержку от имени клиента и
 * признак автоматизации на живом номере, за который мессенджеры ограничивают аккаунты.
 *
 * Клиентские правила (личные чаты УК) сюда не попадают: их заводит сама УК в панели, в
 * настройках своего подключения.
 */
export function IgnoredChatsCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['ignored-chats'], queryFn: fetchVendorIgnored });

  const [channel, setChannel] = useState('max_personal');
  const [kind, setKind] = useState('sender');
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['ignored-chats'] });
  const addMut = useMutation({
    mutationFn: () => addVendorIgnored({ channel, kind, ref: ref.trim(), note: note.trim() || undefined }),
    onSuccess: () => {
      setRef('');
      setNote('');
      toast.success('Правило добавлено');
      void invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось добавить'),
  });
  const removeMut = useMutation({
    mutationFn: removeVendorIgnored,
    onSuccess: () => void invalidate(),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось удалить'),
  });

  const items = q.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Игнорируемые чаты</CardTitle>
        <CardDescription>
          Служебные боты мессенджеров, которым агент не отвечает и переписку которых не берёт
          в базу знаний. Правила общие для всех клиентов; свои личные чаты УК отмечает сама в
          настройках подключения.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <QueryState isLoading={q.isLoading} error={q.error}>
          {items.length === 0 ? (
            <p className="text-muted">Правил нет - агент обрабатывает все входящие.</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {items.map((rule) => (
                <div
                  key={rule.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-line px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm">{rule.note ?? rule.ref}</div>
                    <div className="font-mono text-[11px] text-muted">
                      {rule.channel} · {rule.kind} · {rule.ref}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={removeMut.isPending}
                    onClick={() => {
                      if (confirm(`Удалить правило ${rule.ref}? Агент снова начнёт отвечать в этот чат.`)) {
                        removeMut.mutate(rule.id);
                      }
                    }}
                  >
                    Удалить
                  </Button>
                </div>
              ))}
            </div>
          )}
        </QueryState>

        <form
          className="flex flex-col gap-2 border-t border-line pt-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (ref.trim()) addMut.mutate();
          }}
        >
          <div className="flex flex-wrap gap-3">
            <div className="flex flex-col gap-1">
              <Label>Канал</Label>
              <Segmented
                value={channel}
                onChange={setChannel}
                options={[
                  { value: 'max_personal', label: 'MAX' },
                  { value: 'tg_personal', label: 'Telegram' },
                ]}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>Что блокируем</Label>
              <Segmented
                value={kind}
                onChange={setKind}
                options={[
                  { value: 'sender', label: 'Отправитель' },
                  { value: 'chat', label: 'Чат' },
                ]}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ignored-ref">Идентификатор</Label>
            <Input
              id="ignored-ref"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="например 2340932"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ignored-note">Зачем правило</Label>
            <Input
              id="ignored-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Бот «MAX Поддержка»"
            />
          </div>
          <Button type="submit" size="sm" className="self-start" disabled={!ref.trim() || addMut.isPending}>
            {addMut.isPending ? 'Добавляем…' : 'Добавить правило'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
